//Working code for CircularPercentage

import * as React from 'react'
import { useEffectWithPolling, WidgetWrapper } from 'uxp/components'
import { IUIPropertyDefinitionMap, IWidgetTemplateInstanceProps, IWidgetTemplateProps, registerCardTemplate } from '../../../common/common'
import { generateIconProp } from '../../../common/icon-selector/IconEditor'
import { SelectableItem, selectUIProps } from '../../../common/SelectableItem'
import { ContainerProperty, extractContainerProperties } from '../../../common/ui-props/ContainerProperty'
import { extractTextProperties, TextProperty } from "../../../common/ui-props/TextProperty";
import { CircularPercentageProperty, IPercentageCircleProps } from './CircularPercentageProperty'
import { DataSourceProperty } from "../../../common/ui-props/DataSourceProperty";
import { hasValue, replaceWithData } from '../../../../util'
import './styles.scss'

import useDimensions from 'react-cool-dimensions';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { extractIconProperties, IconProperty } from '../../../common/ui-props/IconProperty'
import { footerProperty, IFooterProps } from './FooterProperty'
import { IContextData, useInitialContextData } from '../../../../hooks/useContextData'
import { executeAndBind, processInitialSource } from '../../../common/data-source-picker/datasource'


const XPR: React.ForwardRefExoticComponent<IWidgetTemplateProps & IPercentageCircleProps & IFooterProps & React.RefAttributes<IWidgetTemplateInstanceProps>> = React.forwardRef((props, ref) => {
    let defaults: IPercentageCircleProps = {
        color: '#ABE087',
        backgroundColor: "#FFF",
        thickness: 10,
        bulletRadius: 15,
        min: 0,
        max: 100,
        channel: "",
        refreshInterval: 6000,
        field: '',
        backgroundCircleColor: '#b5b5b512',
    }

    let footerProps: IFooterProps = {
        firstValueLabel: '10 Units',
        secondValueLabel: 'Active Units',
        firstValueLabelColor: '#000',
        secondValueLabelColor: '#000',
    }
    let minimum = props?.uiProps?.chartProperty?.min || defaults.min
    let maximum = props?.uiProps?.chartProperty?.max || defaults.max

    const { designer } = props;
    let uiProps = props?.uiProps || {};
    const initialSource = props.initialSource;
    const [value, setValue] = React.useState(70);

    let [contextData, setContextData] = React.useState<IContextData>({})
    const [loading, setLoading] = React.useState(false)

    const { isLoading, initialContextData } = useInitialContextData(props)

    React.useEffect(() => {
        setLoading(isLoading)
        setContextData(prev => ({ ...prev, ...initialContextData }))
    }, [isLoading, initialContextData])

    const XPRProps: IUIPropertyDefinitionMap = {
        "headerContainer": ContainerProperty({
            background: {
                color: "",
                image: {
                    image: "",
                    position: "",
                    size: "",
                },
            }
        }),
        'title': TextProperty({
            text: '',
            color: '#424242',
            size: '12px'
        }, 'Title'),
        'subTitle': TextProperty({
            text: 'Meeting',
            color: '',
            align: 'left',
            size: '24px'
        }, 'Sub Title'),
        'icon': IconProperty({ icon: 'solidCalendarAlt', color: '', size: '24px' }),
        'source': DataSourceProperty('Source', uiProps.source, uiProps.datasources, props.uxpContext, props.designer, 'dictionary'),
        'chartProperty': CircularPercentageProperty('CircularPercentage', uiProps.chartProperty || defaults),
        'footerProperty': footerProperty('FooterProperty', uiProps.footerProperty || footerProps)
    }


    let stroke = props?.uiProps?.chartProperty?.thickness || defaults.thickness;
    let { icon, styles: iconStyles } = extractIconProperties(uiProps.icon, 'solidCalendarAlt')
    let { firstValueLabel, firstValueLabelColor, secondValueLabel, secondValueLabelColor } = uiProps.footerProperty as IFooterProps || footerProps
    let { color, backgroundColor, bulletRadius, channel, refreshInterval, backgroundCircleColor } = uiProps.chartProperty as IPercentageCircleProps || defaults
    const { observe, width, height } = useDimensions()

    let { styles: headerStyles } = extractContainerProperties(uiProps.headerContainer)
    let { styles: footerStyles } = extractContainerProperties(uiProps.footerContainer)
    let { text: title, styles: titleStyles } = extractTextProperties(uiProps.title, 'CirclePercentage')
    let { text: subTitle, styles: subTitleStyles } = extractTextProperties(uiProps.subTitle, 'Meeting')

    title = replaceWithData(title, contextData)
    subTitle = replaceWithData(subTitle, contextData)

    function polarToCartesian(centerX: number, centerY: number, radius: number, angleInDegrees: number) {
        var angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;

        return {
            x: centerX + (radius * Math.cos(angleInRadians)),
            y: centerY + (radius * Math.sin(angleInRadians))
        };
    }

    function describeArc(x: number, y: number, radius: number, startAngle: number, endAngle: number, scale: number = 1) {
        var start = polarToCartesian(x, y, radius, endAngle);
        var end = polarToCartesian(x, y, radius, startAngle);

        var largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

        var d = [
            "M", start.x * scale, start.y * scale,
            "A", radius * scale, radius * scale, 0, largeArcFlag, 0, end.x * scale, end.y * scale
        ].join(" ");

        return d;
    }


    React.useImperativeHandle(ref, () => ({
        beforeSave: validateBeforeSave,
        onPreview: validateBeforeSave
    }))

    React.useEffect(() => {
        return () => {
            designer?.removeErrors?.(['datasource', 'field'])
        }
    }, [])

    React.useEffect(() => {
        validateBeforeSave()
    }, [uiProps])


    React.useEffect(() => {

        processInitialSource(props.designer, props.uxpContext, props.uiProps, initialSource, 'dictionary')
            .then(res => {
                if (res) props.designer?.setUIProps(res)
            })
            .catch(e => {
                console.log('exception ', e);

            })

    }, [initialSource]);


    // this will be called before save
    function validateBeforeSave() {

        if (!isDataSourceValid(true)) return false
        if (!isMaxGreaterThanMin(true)) return false
        if (!hasFieldConfigured(true)) return false
        return true
    }

    function isMaxGreaterThanMin(showError?: boolean) {
        if (props?.uiProps?.chartProperty?.min > props?.uiProps?.chartProperty?.max) {
            if (showError) {
                designer?.addErrors?.([{
                    id: 'minmaxerror',
                    type: 'property',
                    error: 'Min cannot be greater than Max',
                    containerId: 'chart-container',
                    fieldId: 'min'
                }])
            }
            return false
        }
        designer?.removeErrors?.(['minmaxerror'])
        return true
    }
    function isDataSourceValid(showError?: boolean) {
        if (!hasValue(uiProps?.source) || !hasValue(uiProps.source?.source) || !hasValue(uiProps.source?.path)) {

            if (showError) {
                designer?.addErrors?.([{
                    id: 'datasource',
                    type: 'property',
                    error: 'Data source is required',
                    containerId: 'chart-container',
                    fieldId: 'source'
                }])
            }

            return false
        }
        designer?.removeErrors?.(['datasource'])
        return true
    }

    function hasFieldConfigured(showError?: boolean) {
        if (!hasValue(uiProps?.chartProperty?.field)) {
            if (showError) {
                designer?.addErrors?.([{
                    id: 'field',
                    type: 'property',
                    error: 'Field is required',
                    containerId: 'chart-container',
                    fieldId: 'field'
                }])
            }
            return false
        }
        designer?.removeErrors?.(['field'])
        return true
    }


    useEffectWithPolling(props.uxpContext, channel || "circularPercentage", refreshInterval || 60000, async () => {
        if (designer?.mode == 'preview' || designer?.mode == 'thumbnail') {
            return;
        }
        executeAndBind(props.uxpContext, uiProps.source, uiProps.datasources, {})
            .then(d => {
                let val = d?.[uiProps.chartProperty?.field || ''];
                setValue(Number(val) || 0)
            })

    }, [props.designer,]);
    let percentageValue = (value - minimum) / (maximum - minimum)

    if (percentageValue <= 0) { percentageValue = 0 }
    if (percentageValue >= 1) { percentageValue = 0.999 }

    return <WidgetWrapper className='uxp-custom-circlepercentage-template'>
        <SelectableItem
            designer={props.designer}
            props={selectUIProps(uiProps, XPRProps, 'headerContainer', 'title')}
            contextData={contextData}
            elementId='header-container'
            style={{ width: "unset", height: "unset" }}>
            <div className="uxp-widget-title-bar uxp-drag-handler" style={headerStyles}>
                <div className="title-section" style={titleStyles}>
                    <span className="title-icon"></span>
                    <span className="txt">{title}</span>
                </div>
            </div>
        </SelectableItem>
        <SelectableItem
            elementId='chart-container'
            designer={props.designer}
            contextData={contextData}
            props={selectUIProps(uiProps, XPRProps, 'source', 'chartProperty')}>
            <div className={'summary '} style={{ backgroundColor: backgroundColor }}>

                <SelectableItem
                    designer={props.designer}
                    contextData={contextData}
                    props={selectUIProps(uiProps, XPRProps, 'source', 'chartProperty')}
                    style={{ height: 'unset', width: 'unset' }} >
                    <div className='percentage-circle'>
                        <div className='value' style={{ backgroundColor: backgroundCircleColor }} ref={observe}>
                            <svg style={{ width: (width + 100), height: (height), position: 'absolute' }} viewBox={`0 0 ${width} ${height}`}>
                                <path
                                    d={describeArc(0.5 * width, 0.5 * height, 0.5 * width - stroke * 0.5, 0, 360 * percentageValue)}
                                    style={{
                                        strokeLinecap: 'round',
                                        strokeWidth: stroke + 'px',
                                    }}
                                    fill="none"
                                    stroke={color}
                                />
                                <circle
                                    className="pointer"
                                    cx={describeArc(0.5 * width, 0.5 * height, 0.5 * width - stroke * 0.5, 0, 360 * percentageValue, 0.99).split(' ')[1]}
                                    cy={describeArc(0.5 * width, 0.5 * height, 0.5 * width - stroke * 0.5, 0, 360 * percentageValue, 0.99).split(' ')[2]}
                                    r={bulletRadius * 0.5}
                                    fill={color}
                                />
                            </svg>

                            <SelectableItem designer={props.designer}
                                contextData={contextData}
                                props={selectUIProps(uiProps, XPRProps, 'icon')} style={{ height: "unset", width: "unset", borderRadius: 'unset' }}>
                                <FontAwesomeIcon icon={generateIconProp(icon)} style={iconStyles} />
                            </SelectableItem>
                            <SelectableItem
                                contextData={contextData}
                                designer={props.designer}
                                props={selectUIProps(uiProps, XPRProps, 'subTitle')} style={{ height: 'unset', width: 'unset', borderRadius: 'unset' }}>
                                <div className="sub-title txt" style={subTitleStyles} >{subTitle}</div>
                            </SelectableItem>
                        </div>
                    </div>
                </SelectableItem>

            </div>
        </SelectableItem>
        <SelectableItem
            designer={props.designer}
            contextData={contextData}
            props={selectUIProps(uiProps, XPRProps, 'footerProperty')}
            style={{ width: "unset", height: "unset" }}>
            <div className="footer-section" style={footerStyles}>

                <div className='sensor-count' style={{ color: firstValueLabelColor }}>{firstValueLabel}</div>

                <span className='units' style={{ color: secondValueLabelColor }}>{secondValueLabel}</span>

            </div>

        </SelectableItem>

    </WidgetWrapper>
})


registerCardTemplate({
    id: 'circular-percentage',
    name: "CircularPercentage card",
    description: 'A widget to show Percentage',
    template: XPR,
    icon: generateIconProp('tachometerAlt'),
    complexity: 'medium',
});
